import { concatU8, le16, le24 } from './bytes.js';
export const A_ACK1 = 0xaa;
export const A_ACK2 = 0xea;
export const C_WRITE_WORD = 0x82;
export const C_READ_WORD = 0xcd;
export const C_WRITE_BLOCK = 0x84;
export const C_READ_BLOCK = 0x85;
export const C_CALL_FUNCTION = 0x9f;
export const C_GETCHECKSUM = 0x33;
export const C_TEST_COMM = 0x93;
export class MinimonError extends Error {
    command;
    constructor(message, command) {
        super(`MiniMon ${command}: ${message}`);
        this.command = command;
        this.name = 'MinimonError';
    }
}
// 24-bit / 16-bit little-endian encoders live in `bytes.ts` as
// `le24` / `le16`. Local aliases keep this file's call sites tight.
const leAddr = le24;
const leWord = le16;
async function expectByte(transport, expected, command, what, timeoutMs) {
    const b = (await transport.read(1, timeoutMs))[0];
    if (b !== expected) {
        throw new MinimonError(`expected ${what}=0x${expected.toString(16).padStart(2, '0').toUpperCase()}, got 0x${b
            .toString(16)
            .padStart(2, '0')
            .toUpperCase()}`, command);
    }
}
export class MinimonClient {
    transport;
    ackTimeoutMs;
    readTimeoutMs;
    constructor(transport, options = {}) {
        this.transport = transport;
        this.ackTimeoutMs = options.ackTimeoutMs ?? 2000;
        this.readTimeoutMs = options.readTimeoutMs ?? 5000;
    }
    /**
     * Test communication with MiniMon. Sends `C_TEST_COMM` and expects
     * `[A_ACK1, A_ACK2]` back. The reference flasher calls this once
     * right after the handshake completes — it's effectively a
     * "MiniMon, are you alive?" probe that primes the comms state.
     */
    async testComm() {
        await this.transport.write(new Uint8Array([C_TEST_COMM]));
        await expectByte(this.transport, A_ACK1, 'testComm', 'A_ACK1', this.ackTimeoutMs);
        await expectByte(this.transport, A_ACK2, 'testComm', 'A_ACK2', this.ackTimeoutMs);
    }
    /** Read one 16-bit word from `addr`. */
    async readWord(addr) {
        await this.transport.write(new Uint8Array([C_READ_WORD]));
        await expectByte(this.transport, A_ACK1, 'readWord', 'A_ACK1', this.ackTimeoutMs);
        await this.transport.write(leAddr(addr));
        const data = await this.transport.read(2, this.readTimeoutMs);
        await expectByte(this.transport, A_ACK2, 'readWord', 'A_ACK2', this.ackTimeoutMs);
        return data[0] | (data[1] << 8);
    }
    /** Write one 16-bit word to `addr`. */
    async writeWord(addr, value) {
        await this.transport.write(new Uint8Array([C_WRITE_WORD]));
        await expectByte(this.transport, A_ACK1, 'writeWord', 'A_ACK1', this.ackTimeoutMs);
        await this.transport.write(concatU8([leAddr(addr), leWord(value)]));
        await expectByte(this.transport, A_ACK2, 'writeWord', 'A_ACK2', this.ackTimeoutMs);
    }
    /** Read `length` bytes from `addr`. */
    async readBlock(addr, length) {
        if (length === 0)
            return new Uint8Array(0);
        await this.transport.write(new Uint8Array([C_READ_BLOCK]));
        await expectByte(this.transport, A_ACK1, 'readBlock', 'A_ACK1', this.ackTimeoutMs);
        await this.transport.write(concatU8([leAddr(addr), leWord(length)]));
        const data = await this.transport.read(length, Math.max(this.readTimeoutMs, length * 2));
        await expectByte(this.transport, A_ACK2, 'readBlock', 'A_ACK2', this.ackTimeoutMs);
        return data;
    }
    /** Write `data` to `addr`. */
    async writeBlock(addr, data) {
        if (data.length === 0)
            return;
        await this.transport.write(new Uint8Array([C_WRITE_BLOCK]));
        await expectByte(this.transport, A_ACK1, 'writeBlock', 'A_ACK1', this.ackTimeoutMs);
        await this.transport.write(concatU8([leAddr(addr), leWord(data.length), data]));
        await expectByte(this.transport, A_ACK2, 'writeBlock', 'A_ACK2', this.ackTimeoutMs);
    }
    /**
     * Call code at `address` passing 8 register words (R0..R7). Returns the
     * 8 register words on return. Long timeout because flash erase /
     * program can take seconds.
     */
    async callFunction(address, registers, timeoutMs = 60_000) {
        if (registers.length !== 8) {
            throw new MinimonError(`callFunction requires exactly 8 register words`, 'callFunction');
        }
        await this.transport.write(new Uint8Array([C_CALL_FUNCTION]));
        await expectByte(this.transport, A_ACK1, 'callFunction', 'A_ACK1', this.ackTimeoutMs);
        const regBytes = concatU8(registers.map((r) => leWord(r)));
        await this.transport.write(concatU8([leAddr(address), regBytes]));
        // Wait for completion, then read returned regs + ACK2.
        const out = await this.transport.read(16, timeoutMs);
        await expectByte(this.transport, A_ACK2, 'callFunction', 'A_ACK2', timeoutMs);
        const result = [];
        for (let i = 0; i < 8; i++)
            result.push(out[i * 2] | (out[i * 2 + 1] << 8));
        return result;
    }
    /** Ask the loader to compute a checksum (Minimon-internal) over a region. */
    async getChecksum(addr, length) {
        await this.transport.write(new Uint8Array([C_GETCHECKSUM]));
        await expectByte(this.transport, A_ACK1, 'getChecksum', 'A_ACK1', this.ackTimeoutMs);
        await this.transport.write(concatU8([leAddr(addr), leWord(length)]));
        const data = await this.transport.read(2, Math.max(this.readTimeoutMs, length * 2));
        await expectByte(this.transport, A_ACK2, 'getChecksum', 'A_ACK2', this.ackTimeoutMs);
        return data[0] | (data[1] << 8);
    }
}
//# sourceMappingURL=minimon.js.map